import type { ServiceCategory, ContactFormValues, ContactSubmission, MediaItem, ContentSection } from '@/types'

const API_BASE_URL = 'https://el-horcon-backend.elhorconcriollo.workers.dev'

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await promise.then(async (value) => {
      clearTimeout(id)
      return value
    })
  } catch (error) {
    clearTimeout(id)
    throw error
  }
}

async function parseJson(response: Response) {
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    let message = text || `HTTP ${response.status}`
    try {
      const json = JSON.parse(text)
      if (json.status === 'error' && json.message) message = json.message
    } catch {
      // keep default message
    }
    throw new Error(message)
  }
  return response.json()
}

export async function fetchServices(): Promise<ServiceCategory[]> {
  const response = await fetch(`${API_BASE_URL}/api/servicios`)
  const json = await parseJson(response)
  if (json.status === 'error') throw new Error(json.message)
  return json.data
}

export async function fetchServiceCategory(idOrName: string | number): Promise<ServiceCategory> {
  const response = await fetch(`${API_BASE_URL}/api/servicios/${encodeURIComponent(String(idOrName))}`)
  const json = await parseJson(response)
  if (json.status === 'error') throw new Error(json.message)
  return json.data
}

export async function submitContact(formData: ContactFormValues): Promise<{ id: number; submitted_at: string }> {
  const response = await fetch(`${API_BASE_URL}/api/contacto`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData),
  })

  const result = await parseJson(response)

  if (result.status === 'error') {
    if (result.error === 'validation_failed') {
      console.error('Errores:', result.details)
    }
    throw new Error(result.message)
  }

  return result.data
}

export class ChatAPI {
  private sessionToken: string | null = null
  private readonly baseUrl = `${API_BASE_URL}/api/ia`

  async sendMessage(message: string, imageBase64?: string): Promise<{ message: string; sessionToken: string; imageAnalyzed?: boolean }> {
    const body: { message: string; session_token?: string; image_base64?: string } = { message }
    if (this.sessionToken) body.session_token = this.sessionToken

    const endpoint = imageBase64 ? '/chat-with-image' : '/chat'
    if (imageBase64) body.image_base64 = imageBase64

    const response = await withTimeout(
      fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
      60000,
    )

    const result = await parseJson(response)

    if (result.status === 'error') {
      if (result.error === 'ai_unavailable') {
        return { message: 'El asistente no está disponible. Por favor, intentá más tarde.', sessionToken: this.sessionToken || '' }
      }
      throw new Error(result.message)
    }

    this.sessionToken = result.data.session_token
    return {
      message: result.data.message,
      sessionToken: result.data.session_token,
      imageAnalyzed: result.data.image_analyzed,
    }
  }

  clearSession() {
    this.sessionToken = null
  }

  setSessionToken(token: string | null) {
    this.sessionToken = token
  }

  getSessionToken(): string | null {
    return this.sessionToken
  }
}

export async function adminLogin(username: string, password: string): Promise<string> {
  const response = await withTimeout(
    fetch(`${API_BASE_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    }),
    10000,
  )

  const result = await parseJson(response)

  if (result.status === 'error') {
    throw new Error(result.message)
  }

  return result.data.session_token
}

export async function adminLogout(): Promise<void> {
  await withTimeout(
    fetch(`${API_BASE_URL}/api/admin/logout`, {
      method: 'POST',
      credentials: 'include',
    }),
    10000,
  )
}

export async function getAdminContent(): Promise<{ pages: ContentSection[]; services: ServiceCategory[]; media: MediaItem[] }> {
  const response = await withTimeout(
    fetch(`${API_BASE_URL}/api/admin/content`, {
      credentials: 'include',
    }),
    10000,
  )

  const result = await parseJson(response)
  return result.data
}

export async function updateContent(section: string, content: Record<string, unknown>): Promise<void> {
  const response = await withTimeout(
    fetch(`${API_BASE_URL}/api/admin/content/${section}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(content),
    }),
    10000,
  )

  const result = await parseJson(response)
  if (result.status === 'error') throw new Error(result.message)
}

export async function uploadImage(file: File, altText: string = ''): Promise<MediaItem> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('alt_text', altText)

  const response = await withTimeout(
    fetch(`${API_BASE_URL}/api/admin/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }),
    10000,
  )

  const result = await parseJson(response)
  if (result.status === 'error') throw new Error(result.message)
  return result.data
}

export async function getContactSubmissions(page: number = 1, limit: number = 20): Promise<{ items: ContactSubmission[]; total: number; page: number; limit: number }> {
  const response = await withTimeout(
    fetch(`${API_BASE_URL}/api/admin/contact-submissions?page=${page}&limit=${limit}`, {
      credentials: 'include',
    }),
    10000,
  )

  const result = await parseJson(response)
  return result.data
}

export async function deleteContactSubmission(id: number): Promise<void> {
  const response = await withTimeout(
    fetch(`${API_BASE_URL}/api/admin/contact-submissions/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }),
    10000,
  )

  const result = await parseJson(response)
  if (result.status === 'error') {
    throw new Error(result.message)
  }
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const dataUrl = reader.result as string
      resolve(dataUrl.split(',')[1])
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}